/**
 * COMPOSER UN PARCOURS — lot P3 (AB#975, épopée AB#961).
 * `/parcours/nouveau?eleve=<id>` ou `?classe=<id>`.
 *
 * Le compositeur de séquence devient un compositeur de parcours : des étapes
 * prises dans PLUSIEURS CHAPITRES d'une même matière, rangées en BLOCS (une
 * nature, un titre facultatif), 20 étapes au plus en tout ; un nom, un mot à
 * l'élève, une date butoir facultative. On le dépose à UN ÉLÈVE ou à UNE
 * CLASSE (un exemplaire par élève). Contrat :
 * `docs/pilotage/passe-ux-prod/CONTRAT-PARCOURS.md` ; maquette :
 * `maquette-parcours/project/Tuteur-Composer.dc.html`.
 *
 * ─── Le même numéro partout ─────────────────────────────────────────────────
 *
 * Chaque étape porte son rang GLOBAL (1 à 7), quel que soit son bloc ; monter
 * ou descendre une étape suit cet ordre et passe d'un bloc à l'autre
 * (fondateur, 19 oct. 2026, AB#961).
 *
 * ─── « Proposer une ébauche » (P4) ──────────────────────────────────────────
 *
 * Le bouton appelle la route d'ébauche de l'élève ; pour une classe, d'après
 * l'un de ses élèves (« Ébauche d'après »), comme la maquette. Tant que la
 * route répond 404, le bouton est masqué (`api.manuelEbaucheServie`). Une
 * ébauche remplace les blocs, rien n'est déposé : le tuteur relit, puis dépose.
 *
 * ─── Ce qui reste comme avant ───────────────────────────────────────────────
 *
 * La banque ne montre que ce qui est EN SERVICE, et le refus du serveur (422)
 * s'affiche tel quel, sans rien perdre de ce qui était composé — comme
 * `ComposerSequencePage`, qui garde la retouche d'une séquence (`?sequence=`).
 * Sous 1 000 px, le même bandeau collant dit ce que le parcours contient.
 */
import {
  LIBELLES_CATEGORIE_EXERCICE,
  LIBELLES_NATURE_BLOC,
  MATIERES,
  MAX_CARACTERES_MOT_PARCOURS,
  MAX_CARACTERES_NOM_PARCOURS,
  MAX_CARACTERES_RAISON_ELEMENT,
  MAX_CARACTERES_TITRE_BLOC,
  MAX_ETAPES_PARCOURS,
  NATURES_BLOC,
  denombrer,
  dureeEnClair,
  type ChapitreProgrammeResumeDto,
  type EleveDuTuteurDto,
  type ExercicesChapitreResponse,
  type GenreDeGeste,
  type Matiere,
  type NatureBloc,
  type NiveauScolaire,
  type NotionDeRessourceDto,
  type SavoirChapitreResponse,
} from '@silex/shared';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { api, type ClasseVue } from '../api/client';
import { Chargement, ErreurRessource } from '../components/EtatRessource';
import { TexteMaths } from '../components/TexteMaths';
import { manuelVivantActif } from '../lib/manuel-vivant';
import {
  adressesManuelEleve,
  adressesParcours,
  ajouterBloc,
  ajouterEtape,
  changerBloc,
  changerRaisonDEtape,
  cleDElement,
  corpsDeposerParcours,
  deplacerEtape,
  etapesDuParcours,
  nomDeNiveau,
  nomDeNotion,
  nombreDEtapes,
  parcoursDEbauche,
  parcoursDeSequence,
  parcoursVide,
  premiersNumeros,
  reglesDuParcours,
  retirerBloc,
  retirerEtape,
  titreDeBloc,
  type BrouillonParcours,
  type RessourceConnue,
} from '../lib/manuel-tuteur-format';
import { lireBrouillonTransmis } from '../lib/manuel-tuteur-lectures';
import { libelleNiveauScolaire, matiereConsole } from '../lib/tuteur-format';
import { messageErreur, useAsync } from '../lib/useAsync';
import { useTuteur } from '../tuteur/TuteurContext';

/* ─────────────────────────── la banque ─────────────────────────── */

/** Une ligne de la banque : de quoi la lire, la filtrer et l'ajouter. */
export interface LigneDeBanque extends RessourceConnue {
  readonly genre: GenreDeGeste;
  readonly code: string;
  /** La ligne grise : niveau, catégorie, notions, durée. */
  readonly sous: string;
  readonly niveau: 1 | 2 | 3 | 4 | null;
}

const GENRES: readonly { readonly genre: GenreDeGeste; readonly libelle: string }[] = [
  { genre: 'capsule', libelle: 'Capsules' },
  { genre: 'fiche', libelle: 'Fiches' },
  { genre: 'exercice', libelle: 'Exercices' },
];

/**
 * Les lignes d'un genre, pour un chapitre : ce qui est en service seulement
 * (une capsule à venir ne s'assigne pas, T40). Pure, pour servir aussi à
 * nommer les étapes d'une ébauche.
 */
export function lignesDeBanque(
  genre: GenreDeGeste,
  exercices: ExercicesChapitreResponse | null,
  savoir: SavoirChapitreResponse | null,
  rangChapitre: number,
): LigneDeBanque[] {
  const nommer = (notions: readonly NotionDeRessourceDto[]): string =>
    notions.map((n) => nomDeNotion(n, rangChapitre)).join(' · ');
  if (genre === 'exercice') {
    return (exercices?.exercices ?? [])
      .filter((e) => e.statut === 'publie')
      .map((e) => ({
        genre,
        code: e.code,
        titre: e.titre ?? e.enonce,
        sorte: `Exercice · ${nomDeNiveau(e.niveauExercice)}`,
        sous: `${nomDeNiveau(e.niveauExercice)} · ${LIBELLES_CATEGORIE_EXERCICE[e.categorie]} · ${nommer([e.notion])}`,
        niveau: e.niveauExercice,
      }));
  }
  if (genre === 'fiche') {
    return (savoir?.fiches ?? [])
      .filter((f) => f.relecture.statut === 'publie')
      .map((f) => ({
        genre,
        code: f.code,
        titre: f.titre,
        sorte: 'Fiche',
        sous: `${f.minutesLecture} min de lecture · ${nommer(f.notions)}`,
        niveau: null,
      }));
  }
  return (savoir?.capsules ?? [])
    .filter((c) => c.relecture.statut === 'publie' && c.etat !== 'a_venir')
    .map((c) => ({
      genre,
      code: c.code,
      titre: c.titre,
      sorte: 'Capsule',
      sous: `${dureeEnClair(c.dureeSec)} · ${nommer(c.notions)}`,
      niveau: null,
    }));
}

/** Les ressources de plusieurs chapitres, par `genre:code` : de quoi nommer une ébauche. */
async function ressourcesDesChapitres(
  chapitres: readonly ChapitreProgrammeResumeDto[],
): Promise<Map<string, RessourceConnue>> {
  const connues = new Map<string, RessourceConnue>();
  await Promise.all(
    chapitres.map(async (c) => {
      const [exercices, savoir] = await Promise.all([
        api.manuelExercicesChapitre(c.code).catch(() => null),
        api.savoirChapitre(c.code).catch(() => null),
      ]);
      for (const g of GENRES) {
        for (const l of lignesDeBanque(g.genre, exercices, savoir, c.rang)) {
          connues.set(cleDElement(l), { titre: l.titre, sorte: l.sorte });
        }
      }
    }),
  );
  return connues;
}

/* ─────────────────────────── la destination ─────────────────────────── */

/** À qui va le parcours : un élève, ou une classe. */
export type Destination =
  | { readonly genre: 'eleve'; readonly id: string }
  | { readonly genre: 'classe'; readonly id: string };

const valeurDe = (d: Destination | null): string => (d === null ? '' : `${d.genre}:${d.id}`);

function destinationDe(valeur: string): Destination | null {
  const [genre, ...reste] = valeur.split(':');
  const id = reste.join(':');
  if (id === '') return null;
  if (genre === 'eleve') return { genre: 'eleve', id };
  if (genre === 'classe') return { genre: 'classe', id };
  return null;
}

/** Les durées qu'on peut demander à l'ébauche, en minutes. */
const MINUTES_EBAUCHE = [30, 45, 60, 90, 120] as const;

/* ─────────────────────────── la page ─────────────────────────── */

export function ComposerParcoursPage() {
  const [params] = useSearchParams();
  const location = useLocation();
  const actif = manuelVivantActif();
  const { eleves } = useTuteur();
  const eleveId = params.get('eleve');
  const classeId = params.get('classe');

  const classes = useAsync(useMemo(() => (actif ? () => api.listerClasses() : null), [actif]));
  const chapitres = useAsync(
    useMemo(() => (actif ? () => api.programmeChapitres() : null), [actif]),
  );
  /* L'élève de départ : son chapitre ouvre la banque, sa matière est celle du parcours. */
  const vue = useAsync(
    useMemo(() => (actif && eleveId ? () => api.manuelVueEleve(eleveId) : null), [actif, eleveId]),
  );

  if (!actif) return <Navigate to="/" replace />;

  const attente =
    eleves.status === 'loading' ||
    classes.status === 'loading' ||
    chapitres.status === 'loading' ||
    (eleveId !== null && vue.status === 'loading');
  const erreur = [eleves, classes, chapitres].find((r) => r.status === 'error');
  const destination: Destination | null =
    eleveId !== null
      ? { genre: 'eleve', id: eleveId }
      : classeId !== null
        ? { genre: 'classe', id: classeId }
        : null;
  const transmis = lireBrouillonTransmis(location.state);
  const retour =
    destination === null
      ? '/eleves'
      : destination.genre === 'eleve'
        ? adressesManuelEleve.fiche(destination.id, 'sequences')
        : adressesParcours.suiviClasse(destination.id);

  return (
    <div className="content__inner manuel-tuteur">
      <p className="note-line fil-retour">
        <Link to={retour}>← Parcours</Link>
      </p>
      {attente && erreur === undefined ? <Chargement quoi="la banque" /> : null}
      {erreur !== undefined && erreur.status === 'error' ? (
        <ErreurRessource message={erreur.message} />
      ) : null}
      {!attente &&
      erreur === undefined &&
      eleves.status === 'ready' &&
      classes.status === 'ready' &&
      chapitres.status === 'ready' ? (
        <ComposerParcours
          eleves={eleves.data.eleves}
          classes={classes.data.classes}
          chapitres={chapitres.data.chapitres}
          destinationInitiale={destination}
          chapitreInitial={vue.status === 'ready' ? vue.data.chapitre.code : null}
          brouillonInitial={transmis === null ? null : parcoursDeSequence(transmis)}
        />
      ) : null}
    </div>
  );
}

/* ─────────────────────────── la composition ─────────────────────────── */

/** La composition, une fois ses listes lues : son état d'arrivée s'éprouve par son rendu. */
export function ComposerParcours({
  eleves,
  classes,
  chapitres,
  destinationInitiale,
  chapitreInitial,
  brouillonInitial,
}: {
  readonly eleves: readonly EleveDuTuteurDto[];
  readonly classes: readonly ClasseVue[];
  readonly chapitres: readonly ChapitreProgrammeResumeDto[];
  readonly destinationInitiale: Destination | null;
  readonly chapitreInitial: string | null;
  readonly brouillonInitial: BrouillonParcours | null;
}) {
  const id = useId();
  const navigate = useNavigate();
  const liste = useRef<HTMLElement>(null);

  const [b, setB] = useState<BrouillonParcours>(() => brouillonInitial ?? parcoursVide());
  const [destination, setDestination] = useState<Destination | null>(destinationInitiale);
  const publies = chapitres.filter((c) => c.statut === 'publie');
  const initial = publies.find((c) => c.code === chapitreInitial);
  const [matiere, setMatiere] = useState<Matiere>(initial?.matiere ?? 'mathematiques');
  const [choisis, setChoisis] = useState<readonly string[]>(
    initial === undefined ? [] : [initial.code],
  );
  const [actif, setActif] = useState<string | null>(initial?.code ?? null);
  const [genre, setGenre] = useState<GenreDeGeste>('capsule');
  const [niveau, setNiveau] = useState('');
  const [blocCible, setBlocCible] = useState<string>(b.blocs[b.blocs.length - 1]?.cle ?? 'b1');
  const [refus, setRefus] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  /* ── la destination, lue ── */
  const eleve =
    destination?.genre === 'eleve'
      ? (eleves.find((e) => e.eleveId === destination.id) ?? null)
      : null;
  const classe =
    destination?.genre === 'classe' ? (classes.find((c) => c.id === destination.id) ?? null) : null;
  const niveaux: ReadonlySet<NiveauScolaire> = new Set(
    eleve !== null
      ? [eleve.niveauScolaire]
      : classe !== null
        ? classe.membres.map((m) => m.niveauScolaire)
        : [],
  );
  /* Le niveau maximal réglé pour l'élève : un exercice au-delà le dit sur sa ligne. */
  const eleveCible = eleve?.eleveId ?? null;
  const vueEleve = useAsync(
    useMemo(
      () => (eleveCible === null ? null : () => api.manuelVueEleve(eleveCible)),
      [eleveCible],
    ),
  );
  const limite =
    vueEleve.status === 'ready' ? (vueEleve.data.niveauMaximal?.niveauMaximal ?? null) : null;

  /* ── l'ébauche : d'après l'élève, ou l'un des élèves de la classe ── */
  const [dApres, setDApres] = useState<string>('');
  const pourEbauche =
    eleve !== null
      ? eleve.eleveId
      : classe !== null
        ? dApres !== '' && classe.membres.some((m) => m.eleveId === dApres)
          ? dApres
          : (classe.membres[0]?.eleveId ?? null)
        : null;
  const servie = useAsync(
    useMemo(
      () => (pourEbauche === null ? null : () => api.manuelEbaucheServie(pourEbauche)),
      [pourEbauche],
    ),
  );
  const [minutes, setMinutes] = useState<number>(60);
  const [ebauche, setEbauche] = useState<'repos' | 'envoi' | string>('repos');

  /* ── les chapitres de la matière : ceux du niveau d'abord, les autres ensuite ── */
  const deLaMatiere = publies
    .filter((c) => c.matiere === matiere)
    .sort((x, y) => (x.niveau === y.niveau ? x.rang - y.rang : x.niveau < y.niveau ? -1 : 1));
  const duNiveau = deLaMatiere.filter((c) => niveaux.has(c.niveau));
  const autres = deLaMatiere.filter((c) => !niveaux.has(c.niveau));
  const matieres = MATIERES.filter((m) => publies.some((c) => c.matiere === m));
  const chapitreActif = deLaMatiere.find((c) => c.code === actif) ?? null;

  const choisirChapitre = (code: string): void => {
    if (code === '' || choisis.includes(code)) return;
    setChoisis([...choisis, code]);
    setActif(code);
  };
  const retirerChapitre = (code: string): void => {
    const reste = choisis.filter((c) => c !== code);
    setChoisis(reste);
    if (actif === code) setActif(reste[0] ?? null);
  };

  /* ── la banque du chapitre ouvert ── */
  const codeActif = chapitreActif?.code ?? null;
  const exercices = useAsync(
    useMemo(
      () => (codeActif === null ? null : () => api.manuelExercicesChapitre(codeActif)),
      [codeActif],
    ),
  );
  const savoir = useAsync(
    useMemo(() => (codeActif === null ? null : () => api.savoirChapitre(codeActif)), [codeActif]),
  );
  useEffect(() => {
    if (genre !== 'exercice') setNiveau('');
  }, [genre]);
  const source = genre === 'exercice' ? exercices : savoir;
  const banque =
    chapitreActif === null
      ? []
      : lignesDeBanque(
          genre,
          exercices.status === 'ready' ? exercices.data : null,
          savoir.status === 'ready' ? savoir.data : null,
          chapitreActif.rang,
        ).filter((l) => niveau === '' || String(l.niveau) === niveau);

  const total = nombreDEtapes(b);
  const plein = total >= MAX_ETAPES_PARCOURS;
  const dedans = new Set(etapesDuParcours(b).map(cleDElement));
  const premiers = premiersNumeros(b);
  const cible = b.blocs.find((bloc) => bloc.cle === blocCible) ?? b.blocs[b.blocs.length - 1];
  const regles = reglesDuParcours(b);
  const toutes = etapesDuParcours(b);
  const derniere = toutes[toutes.length - 1];

  const proposer = async (): Promise<void> => {
    if (pourEbauche === null) return;
    setEbauche('envoi');
    try {
      const reponse = await api.manuelEbaucheParcours(pourEbauche, {
        matiere,
        chapitres: choisis,
        minutes,
        ...(b.pourLe === '' ? {} : { pourLe: b.pourLe }),
      });
      const connues = await ressourcesDesChapitres(
        deLaMatiere.filter((c) => choisis.includes(c.code)),
      );
      const neuf = parcoursDEbauche(reponse, connues, b);
      setB(neuf);
      setBlocCible(neuf.blocs[neuf.blocs.length - 1]?.cle ?? 'b1');
      setEbauche('repos');
    } catch (err) {
      setEbauche(messageErreur(err));
    }
  };

  const deposer = async (): Promise<void> => {
    if (destination === null) return;
    setEnvoi(true);
    setRefus(null);
    try {
      const corps = corpsDeposerParcours(b, matiere);
      if (destination.genre === 'eleve') {
        await api.manuelDeposerParcoursEleve(destination.id, corps);
        navigate(adressesManuelEleve.fiche(destination.id, 'sequences'));
      } else {
        await api.manuelDeposerParcoursClasse(destination.id, corps);
        navigate(adressesParcours.suiviClasse(destination.id));
      }
    } catch (err) {
      setRefus(messageErreur(err));
    } finally {
      setEnvoi(false);
    }
  };

  const nomDestination =
    eleve !== null ? eleve.prenom : classe !== null ? `la classe ${classe.nom}` : null;

  return (
    <>
      <div className="dash-topbar">
        <div>
          <p className="eyebrow">Parcours</p>
          <h1 data-testid="parcours-titre">Nouveau parcours</h1>
        </div>
      </div>

      <div className="tuteur-grid tuteur-grid--egal">
        {/* ── La banque : plusieurs chapitres, un ouvert à la fois ─────────── */}
        <section className="card" data-testid="parcours-banque">
          <h3>Banque</h3>

          <div className="form-grid form-grid--serre">
            {matieres.length > 1 ? (
              <div className="field field--inline">
                <label htmlFor={`${id}-matiere`}>Matière</label>
                <select
                  id={`${id}-matiere`}
                  value={matiere}
                  /* Un parcours est d'UNE matière : on n'en change qu'à vide. */
                  disabled={total > 0}
                  onChange={(e) => {
                    setMatiere(e.target.value as Matiere);
                    setChoisis([]);
                    setActif(null);
                  }}
                  data-testid="parcours-matiere"
                >
                  {matieres.map((m) => (
                    <option key={m} value={m}>
                      {matiereConsole(m)}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            <div className="field field--inline">
              <label htmlFor={`${id}-chapitre`}>Ajouter un chapitre</label>
              <select
                id={`${id}-chapitre`}
                value=""
                onChange={(e) => choisirChapitre(e.target.value)}
                data-testid="parcours-ajouter-chapitre"
              >
                <option value="">Choisir…</option>
                {duNiveau.length > 0 ? (
                  <optgroup label="Son niveau">
                    {duNiveau.map((c) => (
                      <option key={c.code} value={c.code} disabled={choisis.includes(c.code)}>
                        {libelleNiveauScolaire(c.niveau)} · {c.rang} {c.titre}
                      </option>
                    ))}
                  </optgroup>
                ) : null}
                {autres.length > 0 ? (
                  <optgroup label={duNiveau.length > 0 ? 'Autres chapitres' : 'Chapitres'}>
                    {autres.map((c) => (
                      <option key={c.code} value={c.code} disabled={choisis.includes(c.code)}>
                        {libelleNiveauScolaire(c.niveau)} · {c.rang} {c.titre}
                      </option>
                    ))}
                  </optgroup>
                ) : null}
              </select>
            </div>
          </div>

          {choisis.length === 0 ? (
            <p className="note-line">Aucun chapitre choisi.</p>
          ) : (
            <div
              className="segmented"
              role="group"
              aria-label="Chapitre"
              data-testid="parcours-chapitres"
            >
              {deLaMatiere
                .filter((c) => choisis.includes(c.code))
                .map((c) => (
                  <span key={c.code} className="row">
                    <button
                      type="button"
                      className={c.code === actif ? 'segmented__btn is-active' : 'segmented__btn'}
                      aria-pressed={c.code === actif}
                      onClick={() => setActif(c.code)}
                      data-testid="parcours-chapitre"
                    >
                      {libelleNiveauScolaire(c.niveau)} · {c.rang} {c.titre}
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      aria-label={`Retirer le chapitre ${c.titre}`}
                      onClick={() => retirerChapitre(c.code)}
                    >
                      Retirer
                    </button>
                  </span>
                ))}
            </div>
          )}

          {chapitreActif !== null ? (
            <>
              <div className="segmented" role="group" aria-label="Type de ressource">
                {GENRES.map((g) => (
                  <button
                    key={g.genre}
                    type="button"
                    className={g.genre === genre ? 'segmented__btn is-active' : 'segmented__btn'}
                    aria-pressed={g.genre === genre}
                    onClick={() => setGenre(g.genre)}
                    data-testid={`parcours-genre-${g.genre}`}
                  >
                    {g.libelle}
                  </button>
                ))}
              </div>
              {genre === 'exercice' ? (
                <div className="field field--inline">
                  <label htmlFor={`${id}-niveau`}>Niveau d’exercice</label>
                  <select
                    id={`${id}-niveau`}
                    value={niveau}
                    onChange={(e) => setNiveau(e.target.value)}
                  >
                    <option value="">Tous les niveaux</option>
                    {([1, 2, 3, 4] as const).map((n) => (
                      <option key={n} value={n}>
                        {nomDeNiveau(n)}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              {b.blocs.length > 1 && cible !== undefined ? (
                <div className="field field--inline">
                  <label htmlFor={`${id}-cible`}>Ajouter dans</label>
                  <select
                    id={`${id}-cible`}
                    value={cible.cle}
                    onChange={(e) => setBlocCible(e.target.value)}
                    data-testid="parcours-bloc-cible"
                  >
                    {b.blocs.map((bloc, i) => (
                      <option key={bloc.cle} value={bloc.cle}>
                        Bloc {i + 1} · {titreDeBloc(bloc)}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {source.status === 'loading' ? <Chargement quoi="la banque" /> : null}
              {source.status === 'error' ? <ErreurRessource message={source.message} /> : null}
              {source.status === 'ready' && banque.length === 0 ? (
                <p className="note-line">Rien ne correspond à ces filtres.</p>
              ) : null}
              {banque.length > 0 ? (
                <div className="list banque-liste">
                  {banque.map((l) => {
                    const deja = dedans.has(cleDElement(l));
                    const auDela = limite !== null && l.niveau !== null && l.niveau > limite;
                    return (
                      <div
                        key={cleDElement(l)}
                        className="li li--haut"
                        data-testid="parcours-ligne"
                      >
                        <span className="li-main">
                          <span className="li-sub">{l.sous}</span>
                          <span className="banque-liste__titre">
                            <TexteMaths texte={l.titre} />
                          </span>
                          {auDela && eleve !== null ? (
                            <span className="li-sub">
                              Au-delà du niveau que vous avez réglé pour {eleve.prenom}.
                            </span>
                          ) : null}
                        </span>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={deja || plein || cible === undefined}
                          onClick={() =>
                            cible === undefined
                              ? undefined
                              : setB(
                                  ajouterEtape(b, cible.cle, {
                                    genre: l.genre,
                                    code: l.code,
                                    titre: l.titre,
                                    sorte: l.sorte,
                                    raison: '',
                                  }),
                                )
                          }
                          data-testid="parcours-ajouter"
                        >
                          {deja ? 'Ajoutée' : 'Ajouter'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </>
          ) : null}
        </section>

        {/* ── Le parcours ─────────────────────────────────────────────────── */}
        <section className="card" data-testid="parcours-liste" ref={liste}>
          <div className="field">
            <label htmlFor={`${id}-nom`}>Nom du parcours</label>
            <input
              id={`${id}-nom`}
              type="text"
              maxLength={MAX_CARACTERES_NOM_PARCOURS}
              value={b.nom}
              onChange={(e) => setB({ ...b, nom: e.target.value })}
              data-testid="parcours-champ-nom"
            />
          </div>

          {servie.status === 'ready' && servie.data && pourEbauche !== null ? (
            <div className="filters" data-testid="parcours-ebauche">
              {classe !== null ? (
                <label className="field field--inline">
                  <span>Ébauche d’après</span>
                  <select
                    className="control"
                    value={pourEbauche}
                    onChange={(e) => setDApres(e.target.value)}
                  >
                    {classe.membres.map((m) => (
                      <option key={m.eleveId} value={m.eleveId}>
                        {m.prenom}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <label className="field field--inline">
                <span>Durée</span>
                <select
                  className="control"
                  value={minutes}
                  onChange={(e) => setMinutes(Number(e.target.value))}
                >
                  {MINUTES_EBAUCHE.map((m) => (
                    <option key={m} value={m}>
                      {m} min
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="btn btn--ghost"
                disabled={ebauche === 'envoi' || choisis.length === 0}
                onClick={() => void proposer()}
                data-testid="parcours-proposer-ebauche"
              >
                Proposer une ébauche
              </button>
            </div>
          ) : null}
          {ebauche !== 'repos' && ebauche !== 'envoi' ? (
            <div className="callout callout--rare" role="alert">
              <strong>Pas d’ébauche.</strong> {ebauche}
            </div>
          ) : null}

          {b.blocs.map((bloc, iBloc) => (
            <div key={bloc.cle} className="preparation stack-sm" data-testid="parcours-bloc">
              <div className="form-grid form-grid--serre">
                <div className="field field--inline">
                  <label htmlFor={`${id}-${bloc.cle}-titre`}>Nom du bloc {iBloc + 1}</label>
                  <input
                    id={`${id}-${bloc.cle}-titre`}
                    type="text"
                    maxLength={MAX_CARACTERES_TITRE_BLOC}
                    placeholder={LIBELLES_NATURE_BLOC[bloc.nature]}
                    value={bloc.titre}
                    onChange={(e) => setB(changerBloc(b, bloc.cle, { titre: e.target.value }))}
                  />
                </div>
                <div className="field field--inline">
                  <label htmlFor={`${id}-${bloc.cle}-nature`}>Nature</label>
                  <select
                    id={`${id}-${bloc.cle}-nature`}
                    value={bloc.nature}
                    onChange={(e) =>
                      setB(changerBloc(b, bloc.cle, { nature: e.target.value as NatureBloc }))
                    }
                  >
                    {NATURES_BLOC.map((n) => (
                      <option key={n} value={n}>
                        {LIBELLES_NATURE_BLOC[n]}
                      </option>
                    ))}
                  </select>
                </div>
                {b.blocs.length > 1 ? (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => {
                      const neuf = retirerBloc(b, bloc.cle);
                      setB(neuf);
                      setBlocCible(neuf.blocs[neuf.blocs.length - 1]?.cle ?? 'b1');
                    }}
                    data-testid="parcours-retirer-bloc"
                  >
                    Retirer le bloc
                  </button>
                ) : null}
              </div>
              <p className="cell-sub">{denombrer(bloc.etapes.length, 'étape')}</p>

              {bloc.etapes.length === 0 ? (
                <p className="note-line">Bloc vide. Ajoutez depuis la banque.</p>
              ) : (
                <ol className="sequence-elements">
                  {bloc.etapes.map((e, i) => {
                    const numero = (premiers[iBloc] ?? 1) + i;
                    return (
                      <li
                        key={cleDElement(e)}
                        className="sequence-element"
                        data-testid="parcours-etape"
                      >
                        <span className="sequence-element__rang" aria-hidden="true">
                          {numero}
                        </span>
                        <span className="sequence-element__corps">
                          <span className="cell-sub">{e.sorte}</span>
                          <TexteMaths texte={e.titre} />
                          <details className="sequence-raison">
                            <summary>
                              {e.raison.trim() === '' ? 'Dire pourquoi' : e.raison.trim()}
                            </summary>
                            <input
                              type="text"
                              aria-label={`Pourquoi l’étape ${numero} est dans le parcours`}
                              maxLength={MAX_CARACTERES_RAISON_ELEMENT}
                              placeholder="Ajouté par le tuteur"
                              value={e.raison}
                              onChange={(ev) => setB(changerRaisonDEtape(b, e, ev.target.value))}
                            />
                          </details>
                        </span>
                        <span className="sequence-element__gestes">
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            disabled={numero === 1}
                            aria-label={`Monter l’étape ${numero}`}
                            onClick={() => setB(deplacerEtape(b, e, -1))}
                          >
                            Monter
                          </button>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            disabled={numero === total}
                            aria-label={`Descendre l’étape ${numero}`}
                            onClick={() => setB(deplacerEtape(b, e, 1))}
                          >
                            Descendre
                          </button>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            aria-label={`Retirer l’étape ${numero}`}
                            onClick={() => setB(retirerEtape(b, e))}
                          >
                            Retirer
                          </button>
                        </span>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          ))}

          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              const neuf = ajouterBloc(b);
              setB(neuf);
              setBlocCible(neuf.blocs[neuf.blocs.length - 1]?.cle ?? 'b1');
            }}
            data-testid="parcours-ajouter-bloc"
          >
            Ajouter un bloc
          </button>
          <p className="card-sub" data-testid="parcours-compte">
            {denombrer(total, 'étape')} · {MAX_ETAPES_PARCOURS} au plus ·{' '}
            {denombrer(b.blocs.length, 'bloc')}
          </p>

          <div className="field">
            <label htmlFor={`${id}-destination`}>Assigner à</label>
            <select
              id={`${id}-destination`}
              value={valeurDe(destination)}
              onChange={(e) => {
                setDestination(destinationDe(e.target.value));
                setDApres('');
              }}
              data-testid="parcours-destination"
            >
              <option value="">Choisir…</option>
              {eleves.length > 0 ? (
                <optgroup label="Élèves">
                  {eleves.map((e) => (
                    <option key={e.eleveId} value={`eleve:${e.eleveId}`}>
                      {e.prenom} · {libelleNiveauScolaire(e.niveauScolaire)}
                    </option>
                  ))}
                </optgroup>
              ) : null}
              {classes.length > 0 ? (
                <optgroup label="Classes">
                  {classes.map((c) => (
                    <option key={c.id} value={`classe:${c.id}`} disabled={c.nbMembres === 0}>
                      {c.nom} · {denombrer(c.nbMembres, 'élève')}
                    </option>
                  ))}
                </optgroup>
              ) : null}
            </select>
          </div>
          <div className="field">
            <label htmlFor={`${id}-mot`}>
              {classe !== null ? 'Mot aux élèves' : `Mot à ${eleve?.prenom ?? 'l’élève'}`}
            </label>
            <textarea
              id={`${id}-mot`}
              rows={3}
              maxLength={MAX_CARACTERES_MOT_PARCOURS}
              placeholder="Par quoi commencer, à quoi faire attention"
              value={b.mot}
              onChange={(e) => setB({ ...b, mot: e.target.value })}
              data-testid="parcours-champ-mot"
            />
          </div>
          <div className="field">
            <label htmlFor={`${id}-pour-le`}>Date butoir</label>
            <input
              id={`${id}-pour-le`}
              type="date"
              value={b.pourLe}
              onChange={(e) => setB({ ...b, pourLe: e.target.value })}
              data-testid="parcours-champ-pour-le"
            />
          </div>

          {refus !== null ? (
            <div className="callout callout--rare" role="alert" data-testid="parcours-refus">
              <strong>Parcours refusé.</strong> {refus}
            </div>
          ) : null}
          {regles.length > 0 && total > 0 ? (
            <p className="note-line" data-testid="parcours-regles">
              {regles.join(' ')}
            </p>
          ) : null}

          <button
            type="button"
            className="btn btn--primary btn--block"
            disabled={envoi || regles.length > 0 || nomDestination === null}
            onClick={() => void deposer()}
            data-testid="parcours-deposer"
          >
            {nomDestination === null ? 'Assigner' : `Assigner à ${nomDestination}`}
          </button>
        </section>
      </div>

      {/* ── Sous 1 000 px : ce qu'on ajoute reste en vue (`UX-7-15`) ───────── */}
      <div className="composer-bandeau" data-testid="parcours-bandeau" role="status">
        <span className="composer-bandeau__texte">
          <strong>{total === 0 ? 'Parcours vide' : denombrer(total, 'étape')}</strong>
          {derniere === undefined ? null : (
            <span className="composer-bandeau__dernier">
              Dernière ajoutée : <TexteMaths texte={derniere.titre} />
            </span>
          )}
        </span>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => liste.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          data-testid="parcours-voir-liste"
        >
          Voir le parcours
        </button>
      </div>
    </>
  );
}
